import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  MaskedInput,
  Select,
} from "@akuna/ui";
import { toast } from "@/hooks/use-toast";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { useUpdateMember } from "@/hooks/useTeam";
import {
  formatCpf,
  formatDate,
  formatPhone,
  isoToBrDate,
} from "@/lib/format/br";
import { teamErrorKey } from "@/lib/team/api";
import type { TeamMemberDetail } from "@/lib/team/types";
import { getDisplayEmail } from "@/lib/utils/display-email";
import {
  memberDataSchema,
  toUpdateMemberInput,
  type MemberDataForm,
} from "@/lib/validations/team";

function toFormValues(member: TeamMemberDetail): MemberDataForm {
  return {
    full_name: member.full_name,
    cpf: formatCpf(member.cpf),
    birth_date: isoToBrDate(member.birth_date),
    phone: formatPhone(member.phone),
    job_title: member.job_title ?? "",
    employment_type: member.employment_type ?? "",
    hired_at: isoToBrDate(member.hired_at),
    terminated_at: isoToBrDate(member.terminated_at),
    is_veterinarian: member.is_veterinarian,
    is_groomer: member.is_groomer,
  };
}

const card =
  "flex flex-col gap-5 rounded-card border border-border bg-card p-6";

export function MemberDataTab({ member }: { member: TeamMemberDetail }) {
  const { t, i18n } = useTranslation("team");
  const update = useUpdateMember();
  const { hasPermission: canEdit } = useHasPermissionSync("team", "edit");
  const readOnly = member.is_owner || !canEdit;
  const schema = useMemo(() => memberDataSchema(t), [t]);

  const form = useForm<MemberDataForm>({
    resolver: zodResolver(schema),
    defaultValues: toFormValues(member),
  });
  const { errors } = form.formState;

  useEffect(() => {
    form.reset(toFormValues(member));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member.user_id]);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await update.mutateAsync(toUpdateMemberInput(member.user_id, values));
      toast({ title: t("member.saved") });
    } catch (error) {
      toast({ title: t(teamErrorKey(error)), variant: "destructive" });
    }
  });

  const roleAppearsAs =
    member.role_value === "veterinary" || member.role_value === "groomer"
      ? t(`roles.${member.role_value}.name`)
      : null;

  const textField = (
    name: "full_name" | "job_title",
    label: string,
    extra?: { placeholder?: string; hint?: string; full?: boolean },
  ) => (
    <Field
      invalid={!!errors[name]}
      className={extra?.full ? "md:col-span-2" : undefined}
    >
      <FieldLabel>{label}</FieldLabel>
      <Input
        {...form.register(name)}
        placeholder={extra?.placeholder}
        readOnly={readOnly}
      />
      {extra?.hint && <FieldDescription>{extra.hint}</FieldDescription>}
      {errors[name] && <FieldError>{errors[name]?.message}</FieldError>}
    </Field>
  );

  const maskedField = (
    name: "cpf" | "phone" | "birth_date" | "hired_at" | "terminated_at",
    label: string,
    preset: "cpf" | "phone" | "date",
    placeholder?: string,
  ) => (
    <Field invalid={!!errors[name]}>
      <FieldLabel>{label}</FieldLabel>
      <Controller
        control={form.control}
        name={name}
        render={({ field }) => (
          <MaskedInput
            preset={preset}
            value={field.value}
            onValueChange={(masked) => field.onChange(masked)}
            onBlur={field.onBlur}
            placeholder={placeholder}
            readOnly={readOnly}
            inputMode="numeric"
          />
        )}
      />
      {errors[name] && <FieldError>{errors[name]?.message}</FieldError>}
    </Field>
  );

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="flex flex-wrap items-start gap-6"
    >
      <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-6">
        {readOnly && (
          <p className="rounded-md bg-state-warning-soft px-4 py-3 text-sm text-state-warning-foreground">
            {t("data.ownerLocked")}
          </p>
        )}
        <section className={card} aria-labelledby="team-personal">
          <h2 id="team-personal" className="text-base font-semibold">
            {t("data.personal")}
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {textField("full_name", t("data.fullName"), { full: true })}
            {maskedField("cpf", t("data.cpf"), "cpf", "000.000.000-00")}
            {maskedField(
              "birth_date",
              t("data.birthDate"),
              "date",
              t("data.datePlaceholder"),
            )}
            {maskedField("phone", t("data.phone"), "phone", "(00) 00000-0000")}
            <Field>
              <FieldLabel>{t("data.email")}</FieldLabel>
              <Input value={getDisplayEmail(member.email) ?? ""} readOnly />
              <FieldDescription>{t("data.emailHint")}</FieldDescription>
            </Field>
          </div>
        </section>

        <section className={card} aria-labelledby="team-bond">
          <h2 id="team-bond" className="text-base font-semibold">
            {t("data.bond")}
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {textField("job_title", t("data.jobTitle"), {
              placeholder: t("data.jobTitlePlaceholder"),
              hint: t("data.jobTitleHint"),
            })}
            <Controller
              control={form.control}
              name="employment_type"
              render={({ field }) => (
                <Select
                  forceNative
                  label={t("data.employmentType")}
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={readOnly}
                  options={[
                    { value: "", label: t("data.employmentPlaceholder") },
                    ...(["clt", "pj", "autonomo", "estagio"] as const).map(
                      (v) => ({ value: v, label: t(`employment.${v}`) }),
                    ),
                  ]}
                />
              )}
            />
            {maskedField(
              "hired_at",
              t("data.hiredAt"),
              "date",
              t("data.datePlaceholder"),
            )}
            {maskedField(
              "terminated_at",
              t("data.terminatedAt"),
              "date",
              t("data.optional"),
            )}
          </div>
          <fieldset className="flex flex-col gap-2.5">
            <legend className="mb-2.5 text-sm font-medium">
              {t("data.professional")}
            </legend>
            {roleAppearsAs ? (
              <p className="text-sm text-muted-foreground">
                {t("data.professionalByRole", { role: roleAppearsAs })}
              </p>
            ) : (
              <div className="flex flex-wrap gap-2.5">
                <Controller
                  control={form.control}
                  name="is_veterinarian"
                  render={({ field }) => (
                    <Checkbox
                      label={t("data.professionalVet")}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={readOnly || member.role_value !== "admin"}
                    />
                  )}
                />
                <Controller
                  control={form.control}
                  name="is_groomer"
                  render={({ field }) => (
                    <Checkbox
                      label={t("data.professionalGroomer")}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={readOnly || member.role_value !== "admin"}
                    />
                  )}
                />
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {t("data.professionalHint")}
            </p>
          </fieldset>
        </section>

        {!readOnly && (
          <div className="flex justify-end">
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? t("member.saving") : t("member.save")}
            </Button>
          </div>
        )}
      </div>

      <aside className="flex min-w-0 flex-[1_1_320px] flex-col gap-4">
        <section className={card} aria-labelledby="team-access">
          <div className="flex flex-col gap-1">
            <h2 id="team-access" className="text-base font-semibold">
              {t("data.access")}
            </h2>
            <span className="text-sm text-muted-foreground">
              {t("data.accessHint")}
            </span>
          </div>
          <dl className="flex flex-col gap-2 rounded-md bg-muted p-3.5 text-sm">
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">{t("data.login")}</dt>
              <dd className="truncate">{getDisplayEmail(member.email) ?? "—"}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">{t("data.role")}</dt>
              <dd>{t(`roles.${member.role_value}.name`)}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-muted-foreground">{t("data.lastSignIn")}</dt>
              <dd>
                {member.last_sign_in_at
                  ? formatDate(member.last_sign_in_at, i18n.language)
                  : t("data.never")}
              </dd>
            </div>
          </dl>
          <Button asChild variant="outline">
            <Link to={`/team/${member.user_id}/permissoes`}>
              {t("data.adjustPermissions")}
            </Link>
          </Button>
        </section>
      </aside>
    </form>
  );
}
